import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star, ThumbsUp } from 'lucide-react';
import type { FeedReview } from './types';

export default function FriendsWidget() {
  const [, navigate] = useLocation();

  const { data: feedData, isLoading } = useQuery<FeedReview[]>({
    queryKey: ['/api/feed/reviews', 'following'],
    queryFn: async () => {
      const res = await fetch('/api/feed/reviews?filterTags=following');
      if (!res.ok) throw new Error('Failed to fetch feed');
      return res.json() as Promise<FeedReview[]>;
    },
    staleTime: 2 * 60_000,
  });

  const reviews = feedData ?? [];

  if (isLoading) {
    return (
      <div className="px-4 space-y-3">
        {[1, 2].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="px-4 py-2 text-center text-sm text-muted-foreground">
        Follow friends to see their latest reviews here.
        <Button variant="ghost" size="sm" className="pl-1 text-primary" onClick={() => navigate('/friends')}>Find friends</Button>
      </div>
    );
  }

  return (
    <div className="px-4 space-y-3">
      {reviews.slice(0, 3).map((review) => (
        <Card key={review.id} className="hover-elevate active-elevate-2 cursor-pointer" onClick={() => navigate('/')}>
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <Avatar className="w-8 h-8 flex-shrink-0">
                {review.author?.avatar && <AvatarImage src={review.author.avatar} alt={review.author.name ?? ''} />}
                <AvatarFallback className="text-xs">{(review.author?.name ?? 'U').charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="font-medium text-xs truncate">{review.author?.displayName ?? review.author?.name ?? 'Someone'}</span>
                  <span className="text-xs text-muted-foreground">at</span>
                  <span className="font-medium text-xs truncate text-primary">{review.restaurant?.name ?? 'a restaurant'}</span>
                </div>
                {review.text && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{review.text}</p>}
                <div className="flex items-center gap-2 mt-1">
                  {review.overallRating != null && (
                    <div className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-primary text-primary" />
                      <span className="text-xs font-medium">{review.overallRating}</span>
                    </div>
                  )}
                  {review.likesCount != null && (
                    <div className="flex items-center gap-0.5 text-muted-foreground">
                      <ThumbsUp className="w-3 h-3" />
                      <span className="text-xs">{review.likesCount}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
